import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import LegalBody from '../components/legal/LegalBody';
import { legalApi, LegalDocument } from '../services/api';
import { openCookiePreferences } from '../utils/cookieConsent';

/**
 * Hukuki metin sayfası — /aydinlatma-metni, /cerez-politikasi, /cerez-listesi.
 *
 * Rota bilerek GİRİŞ GEREKTİRMİYOR ve DashboardLayout'un dışında: aydınlatma
 * metni, kişi daha hesap açmadan önce okunabilir olmak zorunda (KVKK m.10 —
 * aydınlatma, veri işleme başlamadan yapılır). Ayrıca App Store ve Google Play,
 * gizlilik politikasının uygulama dışından açılabilen bir URL'de yayımlanmasını
 * şart koşuyor; mağaza formuna verilecek adres bu sayfadır.
 */

/** URL yolu = sunucudaki belge slug'ı; App.tsx üç rotayı da buraya bağlar. */
export type LegalSlug = 'aydinlatma-metni' | 'cerez-politikasi' | 'cerez-listesi';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

const LegalPage: React.FC<{ slug: LegalSlug }> = ({ slug }) => {
  const navigate = useNavigate();
  const [doc, setDoc] = useState<LegalDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setError(null);
    legalApi
      .document(slug)
      .then((res) => {
        if (!cancelled) setDoc(res.data);
      })
      .catch(() => {
        if (!cancelled) setError('Belge şu anda görüntülenemiyor. Lütfen daha sonra tekrar deneyin.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (doc) document.title = `${doc.short_title} — bryekosis LMS`;
    return () => {
      document.title = 'bryekosis LMS';
    };
  }, [doc]);

  return (
    <div className="min-h-screen bg-[#f4f6fa]">
      <header className="border-b border-[#e6e9ef] bg-white">
        <div className="mx-auto flex max-w-[880px] items-center justify-between gap-4 px-5 py-4">
          <button type="button" onClick={() => navigate(-1)} className="shrink-0" aria-label="Geri dön">
            <BrandLogo variant="mark" className="h-[34px] object-contain" />
          </button>
          <nav className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-[13px]">
            <button
              type="button"
              onClick={() => navigate('/aydinlatma-metni')}
              className={`font-medium hover:underline ${slug === 'aydinlatma-metni' ? 'text-[#3973bf]' : 'text-[#5b6472]'}`}
            >
              Aydınlatma Metni
            </button>
            <button
              type="button"
              onClick={() => navigate('/cerez-politikasi')}
              className={`font-medium hover:underline ${slug === 'cerez-politikasi' ? 'text-[#3973bf]' : 'text-[#5b6472]'}`}
            >
              Çerez Politikası
            </button>
            <button
              type="button"
              onClick={() => navigate('/cerez-listesi')}
              className={`font-medium hover:underline ${slug === 'cerez-listesi' ? 'text-[#3973bf]' : 'text-[#5b6472]'}`}
            >
              Çerez Listesi
            </button>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="font-medium text-[#5b6472] hover:underline"
            >
              Çerez Tercihleri
            </button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[880px] px-5 py-8 lg:py-12">
        <div className="rounded-[20px] border border-[#e6e9ef] bg-white p-6 shadow-[0_10px_40px_rgba(20,30,50,0.05)] lg:p-10">
          {error && <p className="text-[14px] text-[#c2410c]">{error}</p>}

          {!error && !doc && (
            <p className="text-[14px] text-[#8a93a2]">Yükleniyor…</p>
          )}

          {doc && (
            <>
              <h1 className="text-[20px] font-bold leading-snug text-[#2b2b2b] lg:text-[24px]">
                {doc.title}
              </h1>
              <p className="mt-2 mb-8 text-[12.5px] text-[#8a93a2]">
                Yürürlük tarihi: {formatDate(doc.effective_date)} · Sürüm {doc.version}
              </p>
              <LegalBody body={doc.body} />
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-[13px] font-semibold text-[#3973bf] hover:underline"
          >
            ← Geri dön
          </button>
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
